import { DatePipe, NgFor, NgIf } from '@angular/common';
import {
	Component,
	EventEmitter,
	Input,
	OnChanges,
	OnDestroy,
	Output
} from '@angular/core';
import { ChartConfiguration } from 'chart.js';
import { NgChartsModule } from 'ng2-charts';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { IconComponent, TypographyComponent } from 'components';
import { ChartConfigService } from '@brand-lolly/app/social-media-influencer/modules/discovery-v2/components/discovery-profile/services/chart-config.service';
import { GetSocialListeningMyBrandAnalyticsSentimentService } from '@brand-lolly/app/social-media-influencer/modules/social-listening/services/get-social-listening-my-brand-analytics-sentiment.service';
import { ReviewSentimentResponse } from '@brand-lolly/app/social-media-influencer/modules/social-listening/models/social-listening-reviews.model';
import {
	ChartWidget,
	PreviewSlide,
	ReportDateRange,
	SummaryTextWidget,
	TitleWidget
} from '../../../../models/preview/preview.models';
import { ReportContentItem } from '../../../../services/report-settings.service';
import {
	applyContentItemOverrides,
	hasPositiveDataPointTotal
} from '../../../../services/report-preview-utils';
import { SlideNoDataComponent } from '../slide-no-data/slide-no-data.component';

// Sentiment series palette — mapped to the shared report tokens
// ($positive / $muted / $negative) so the chart lines match the KPI dots.
const SENTIMENT_COLORS = {
	positive: '#2bb673',
	neutral: '#6b7796',
	negative: '#e94f6a'
};

interface LegendChip {
	id: string;
	label: string;
	color: string;
}

import { prepareForDrawing } from '../../../../services/report-preview-chart-configs';
import { ReportInlineTextComponent } from '../../components/report-inline-text/report-inline-text.component';
import {
	NO_SLIDE_TEXT_FIELDS,
	ReportSlideTextFields,
	slideTextFields
} from '../../../../services/report-inline-edit.service';
import { ReportBlockRender } from '../../../../services/report-blocks.service';
import {
	ReportSectionChartSwitch,
	sectionChartSource,
	sectionChartSwitch
} from '../../../../services/report-chart-types';
import { ReportChartSwitchComponent } from '../../components/report-chart-switch/report-chart-switch.component';
import { ReportSlideBrandComponent } from '../../components/report-slide-brand/report-slide-brand.component';
@Component({
	standalone: true,
	selector: 'app-sentiment-over-time-slide',
	templateUrl: './sentiment-over-time-slide.component.html',
	styleUrls: ['./sentiment-over-time-slide.component.scss'],
	imports: [
		NgFor,
		NgIf,
		IconComponent,
		TypographyComponent,
		NgChartsModule,
		SlideNoDataComponent,
		ReportInlineTextComponent,
		ReportChartSwitchComponent,
		ReportSlideBrandComponent
	],
	providers: [DatePipe]
})
export class SentimentOverTimeSlideComponent implements OnChanges, OnDestroy {
	@Input() item!: ReportContentItem;
	@Input() brandId!: string;
	@Input() dateRange!: ReportDateRange;
	@Input() themeColor: string | null = null;
	@Input() editorMode = false;
	@Output() slideBuilt = new EventEmitter<PreviewSlide>();
	/** The reader picked another type for this section's chart. */
	@Output() chartTypeChange = new EventEmitter<ReportBlockRender | null>();

	/** The heading and summary the reader can type into, in the editor. */
	textFields: ReportSlideTextFields = NO_SLIDE_TEXT_FIELDS;

	title = '';
	titleColor = '#2872e1';
	dateLabel = '';
	summaryText = '';
	chartData: ChartConfiguration<'line'>['data'] | null = null;
	chartOptions: ChartConfiguration<'line'>['options'] | null = null;
	// Follows the data: a period too short for a line is drawn as bars.
	chartType: 'line' | 'bar' = 'line';
	legendChips: LegendChip[] = [];
	// Top-right KPI callout: positive share of all sentiment.
	positiveShare = '—';
	readonly kpiDotColor = '#2872e1';
	/** What the chart is drawn as: its own design until the reader switches. */
	chartSwitch: ReportSectionChartSwitch = sectionChartSwitch(
		'sentiment_over_time',
		null,
		null
	);

	private readonly destroy$ = new Subject<void>();
	private cachedSentimentData: ReviewSentimentResponse | null = null;
	private cacheKey: string | null = null;

	get isEmpty(): boolean {
		return (
			!this.cachedSentimentData ||
			![
				this.cachedSentimentData.negativeSentimentData,
				this.cachedSentimentData.neutralSentimentData,
				this.cachedSentimentData.positiveSentimentData
			].some(series => hasPositiveDataPointTotal(series))
		);
	}

	constructor(
		private readonly sentimentService: GetSocialListeningMyBrandAnalyticsSentimentService,
		private readonly chartConfigService: ChartConfigService,
		private readonly datePipe: DatePipe
	) {}

	trackByChipId(_index: number, chip: LegendChip): string {
		return chip.id;
	}

	ngOnChanges(): void {
		this.destroy$.next();
		if (!this.item || !this.brandId || !this.dateRange) return;

		const currentCacheKey = `${this.brandId}:${this.dateRange.start}:${this.dateRange.end}`;
		if (this.cachedSentimentData && this.cacheKey === currentCacheKey) {
			this.applySentimentData(this.cachedSentimentData);
			return;
		}

		this.sentimentService
			// Scope the series to the report's selected period (from/to) —
			// without it the endpoint defaults to all-time.
			.fetch(
				this.brandId,
				new Date(this.dateRange.start),
				undefined,
				new Date(this.dateRange.end)
			)
			.pipe(takeUntil(this.destroy$))
			.subscribe(data => {
				this.cachedSentimentData = data;
				this.cacheKey = currentCacheKey;
				this.applySentimentData(data);
			});
	}

	private applySentimentData(data: ReviewSentimentResponse): void {
		const rawSlide = this.buildSlide(data);
		const slide = applyContentItemOverrides(
			rawSlide,
			this.item,
			this.themeColor
		);
		this.updateViewModel(slide);
		this.slideBuilt.emit(slide);
	}

	ngOnDestroy(): void {
		this.destroy$.next();
		this.destroy$.complete();
	}

	private buildSlide(data: ReviewSentimentResponse): PreviewSlide {
		const prefix = `${this.item.id}_${this.brandId}`;

		// Order matches the legend chips in the design: Positive, Neutral,
		// Negative.
		const seriesDefinitions = [
			{
				label: 'Positive',
				color: SENTIMENT_COLORS.positive,
				data: data.positiveSentimentData
			},
			{
				label: 'Neutral',
				color: SENTIMENT_COLORS.neutral,
				data: data.neutralSentimentData
			},
			{
				label: 'Negative',
				color: SENTIMENT_COLORS.negative,
				data: data.negativeSentimentData
			}
		];

		const labels = seriesDefinitions[0].data.map(
			point => this.datePipe.transform(point.timestamp, 'dd MMM') ?? ''
		);

		const datasets = seriesDefinitions.map(series => ({
			label: series.label,
			data: series.data.map(point => point.value),
			borderColor: series.color,
			backgroundColor: series.color,
			pointBackgroundColor: series.color,
			pointBorderColor: 'transparent' as const,
			fill: false,
			borderWidth: 2,
			pointRadius: 0,
			pointHoverRadius: 3,
			tension: 0.4,
			type: 'line' as const
		}));

		const chartJsConfig: ChartConfiguration<'line'> = {
			type: 'line',
			data: { labels, datasets },
			options: {
				responsive: true,
				maintainAspectRatio: false,
				plugins: {
					legend: { display: false },
					tooltip: this.chartConfigService.getTooltipConfig() as any
				},
				scales: {
					x: {
						offset: false,
						border: { display: false },
						grid: { display: false },
						ticks: {
							color: '#6b7796',
							font: { family: 'Gilroy', size: 11 },
							maxRotation: 0,
							autoSkip: true,
							autoSkipPadding: 16
						}
					},
					y: {
						beginAtZero: true,
						border: { display: false },
						grid: { color: '#e6eaf2', drawTicks: false },
						ticks: {
							color: '#6b7796',
							font: { family: 'Gilroy', size: 11 },
							padding: 8
						}
					}
				} as any
			}
		};

		const chartWidget: ChartWidget = {
			id: `${prefix}_chart`,
			kind: 'chart',
			visible: true,
			content: {
				chartType: 'line',
				dataSourceKey: 'sentiment_over_time',
				seriesMeta: seriesDefinitions.map(series => ({
					key: series.label.toLowerCase(),
					label: series.label,
					color: series.color
				})),
				chartJsConfig: chartJsConfig as unknown as Record<
					string,
					unknown
				>
			}
		};

		return {
			id: `${prefix}_slide`,
			slideType: 'sentiment_over_time',
			title: this.item.title || 'Sentiment Over Time',
			description: this.item.description || '',
			dateRange: this.dateRange,
			widgets: [
				{
					id: `${prefix}_title`,
					kind: 'title',
					visible: true,
					content: {
						text: this.item.title || 'Sentiment Over Time',
						color: '#2872e1'
					}
				} as TitleWidget,
				{
					id: `${prefix}_date`,
					kind: 'date_range',
					visible: true,
					content: { text: this.dateRange.label }
				} as TitleWidget,
				{
					id: `${prefix}_summary`,
					kind: 'summary_text',
					visible: true,
					content: {
						text:
							this.item.description ||
							'Sentiment trends across all monitored channels.'
					}
				} as SummaryTextWidget,
				chartWidget
			]
		};
	}

	private updateViewModel(slide: PreviewSlide): void {
		this.textFields = slideTextFields(slide);
		const hidden = new Set(slide.overrides?.hiddenWidgetIds ?? []);

		const titleWidget = slide.widgets.find(
			widget => widget.kind === 'title' && !hidden.has(widget.id)
		) as TitleWidget | undefined;
		const dateWidget = slide.widgets.find(
			widget => widget.kind === 'date_range' && !hidden.has(widget.id)
		) as TitleWidget | undefined;
		const summaryWidget = slide.widgets.find(
			widget => widget.kind === 'summary_text' && !hidden.has(widget.id)
		) as SummaryTextWidget | undefined;

		this.title = titleWidget?.content.text ?? slide.title;
		this.titleColor = titleWidget?.content.color ?? '#2872e1';
		this.dateLabel =
			dateWidget?.content.text ?? slide.dateRange?.label ?? '';
		this.summaryText = summaryWidget?.content.text ?? '';

		const chartWidget = (slide.widgets.find(
			widget => widget.kind === 'chart' && !hidden.has(widget.id)
		) ?? null) as ChartWidget | null;

		if (chartWidget) {
			const config = chartWidget.content
				.chartJsConfig as unknown as ChartConfiguration<'line'>;
			this.chartData = config?.data ?? null;
			if (config) {
				this.chartType = prepareForDrawing(
					config as unknown as ChartConfiguration
				) as 'line' | 'bar';
			}
			this.chartOptions = config?.options ?? null;

			const seriesMeta = chartWidget.content.seriesMeta ?? [];
			const datasets = config?.data?.datasets ?? [];
			const sums = datasets.map(dataset =>
				(dataset.data as number[]).reduce(
					(accumulator, value) => accumulator + value,
					0
				)
			);
			const total = sums.reduce(
				(accumulator, sum) => accumulator + sum,
				0
			);

			// Positive is the first series (see buildSlide ordering).
			const positiveSum = sums[0] ?? 0;
			this.positiveShare =
				total > 0
					? `${((positiveSum / total) * 100).toFixed(1)}%`
					: '—';

			this.legendChips = seriesMeta.map(meta => ({
				id: meta.key ?? meta.label,
				label: meta.label,
				color: meta.color ?? ''
			}));
		} else {
			this.chartData = null;
			this.chartOptions = null;
			this.legendChips = [];
			this.positiveShare = '—';
		}

		// The three sentiment lines, in the colours the chips above name —
		// so the chips stay the legend whatever the chart is redrawn as.
		const datasets = this.chartData?.datasets ?? [];
		this.chartSwitch = sectionChartSwitch(
			this.item.id,
			this.item.chartType,
			datasets.length
				? sectionChartSource(this.item.id, {
						labels: (this.chartData?.labels ?? []) as string[],
						series: datasets.map(dataset => ({
							key: String(dataset.label ?? '').toLowerCase(),
							label: String(dataset.label ?? ''),
							values: dataset.data as number[],
							color: dataset.borderColor as string
						})),
						seriesLegendShown: true
				  })
				: null
		);
	}
}
